import { gql } from 'graphql-request';
import { graphQLClient } from '@devd-client/api';
import { getOrgId } from '@devd-client/devd/auth';
import {
  ReportQuestionDetailsResponse,
  ReportDetailsResponse,
  SprintMetricsByTypeResponse,
  TeamDashboardDetailsResponse,
  CustomReportMetricByTypeResponse,
  TeamHygieneTicketDetailsResponse,
  EngineeringStatsResponse,
  ProductStatsResponse,
  ReworkDensityResponse,
  WorkflowStageTimeResponse,
} from '../helpers/report.types';

export const getReportQuestionsGraphql = async (
  pageNumber: number,
  pageSize: number
): Promise<ReportQuestionDetailsResponse> =>
  await graphQLClient.request(
    gql`
      query GetReportQuestionDetails(
        $orgId: String
        $pageNumber: Int!
        $pageSize: Int!
      ) {
        ReportQuestionDetails(
          orgId: $orgId
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            id
            question
            icon
            questionType
            description
          }
        }
      }
    `,
    {
      orgId: getOrgId(),
      pageNumber,
      pageSize,
    }
  );

export const getReportQuestionDetailsGraphql = async (
  reportId: string,
  startDate: string,
  endDate: string,
  sprintId: string
): Promise<ReportDetailsResponse> =>
  await graphQLClient.request(
    gql`
      query GetReportDetails(
        $reportId: String!
        $startDate: String!
        $endDate: String!
        $orgId: String!
        $sprintId: String!
        $timeZone: String
      ) {
        ReportDetails(
          reportId: $reportId
          filter: {
            orgId: $orgId
            startDate: $startDate
            endDate: $endDate
            sprintId: $sprintId
            timeZone: $timeZone
          }
        ) {
          metadata {
            zeroStates {
              type
              url
            }
          }
          reportId
          reports {
            headline
            reportMetrics {
              metric
              suggestedActions {
                text
                cta
              }
              status
              zeroState
            }
            suggestedActions {
              text
              cta
            }
          }
        }
      }
    `,
    {
      reportId,
      orgId: getOrgId(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startDate,
      endDate,
      sprintId,
    }
  );

export const sprintGraphql = async (
  metric: string,
  teamId: string,
  startDate: string,
  endDate: string,
  sprintId: string
): Promise<SprintMetricsByTypeResponse> =>
  await graphQLClient.request(
    gql`
      query GetSprintMetricsByType(
        $metric: String!
        $teamId: String!
        $startDate: String!
        $endDate: String!
        $sprintId: String!
        $orgId: String!
        $timeZone: String
      ) {
        SprintMetricsByType(
          metric: $metric
          granularity: ""
          filter: {
            orgId: $orgId
            teamId: $teamId
            startDate: $startDate
            endDate: $endDate
            sprintId: $sprintId
            timeZone: $timeZone
          }
        ) {
          chartMetadata {
            chartType
            chartTitle
            xlabel
            ylabel
            xAxis
            yAxis
            info
            chartKey
            isFavMetric
          }
          data
          keys {
            key
            name
            color
          }
          tags
          highlight
          relatedMetrics
          average
          averageLabel
          chartDataState
          chartError {
            message
            link
          }
        }
      }
    `,
    {
      metric,
      teamId,
      orgId: getOrgId(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startDate,
      endDate,
      sprintId,
    }
  );

export const scopeChangeMetricGraphql = async (
  metric: string,
  teamId: string,
  startDate: string,
  endDate: string,
  sprintId: string
): Promise<any> =>
  await graphQLClient.request(
    gql`
      query GetSprintMetricsByType(
        $metric: String!
        $orgId: String!
        $teamId: String!
        $startDate: String!
        $endDate: String!
        $sprintId: String!
        $timeZone: String
      ) {
        SprintMetricsByType(
          metric: $metric
          granularity: ""
          filter: {
            orgId: $orgId
            startDate: $startDate
            endDate: $endDate
            teamId: $teamId
            sprintId: $sprintId
            timeZone: $timeZone
          }
        ) {
          chartMetadata {
            chartType
            chartTitle
            xlabel
            ylabel
            xAxis
            yAxis
            info
          }
          data
          keys {
            key
            name
            color
          }
          average
          previousPeriodStat {
            percentChange
            improved
            reason
          }
          highlight
          chartDataState
          chartError {
            message
            link
          }
        }
      }
    `,
    {
      metric,
      orgId: getOrgId(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      teamId,
      startDate,
      endDate,
      sprintId,
    }
  );

export const issueTimelineDistributionGraphql = async (
  startDate: string,
  endDate: string,
  teamId: string,
  sprintId: string,
  type: string
): Promise<any> =>
  await graphQLClient.request(
    gql`
      query GetCustomReportMetricByType(
        $orgId: String!
        $startDate: String!
        $endDate: String!
        $teamId: String!
        $sprintId: String!
        $type: String!
        $metric: String!
        $timeZone: String
      ) {
        CustomReportMetricByType(
          metric: $metric
          filter: {
            orgId: $orgId
            startDate: $startDate
            endDate: $endDate
            teamId: $teamId
            sprintId: $sprintId
            type: $type
            timeZone: $timeZone
          }
        ) {
          chartMetadata {
            chartKey
            chartType
            chartTitle
            xlabel
            ylabel
            xAxis
            yAxis
            zAxis
            zlabel
            info
            zsImage
            source
          }
          data
          keys {
            key
            name
            color
            chartType
          }
          chartDataState

          chartError {
            message
            link
          }
          threshold {
            title
            value
            color
            desc
            target
          }
        }
      }
    `,
    {
      orgId: `${getOrgId()}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startDate: startDate,
      endDate: endDate,
      teamId: teamId,
      sprintId: sprintId,
      type: type,
      metric: 'ISSUE_DISTRIBUTION',
    }
  );

export const contributorRankingGraphql = async (
  startDate: string,
  endDate: string,
  teamId: string,
  sprintId: string,
  metric: string,
  jobRole: string
): Promise<CustomReportMetricByTypeResponse> =>
  await graphQLClient.request(
    gql`
      query GetCustomReportMetricByType(
        $orgId: String!
        $startDate: String!
        $endDate: String!
        $teamId: String!
        $sprintId: String!
        $metric: String!
        $jobRole: String!
        $timeZone: String
      ) {
        CustomReportMetricByType(
          filter: {
            orgId: $orgId
            startDate: $startDate
            endDate: $endDate
            teamId: $teamId
            sprintId: $sprintId
            jobRole: $jobRole
            timeZone: $timeZone
          }
          metric: $metric
        ) {
          chartMetadata {
            chartKey
            chartType
            chartTitle
            xlabel
            ylabel
            xAxis
            yAxis
            zAxis
            zlabel
            info
            zsImage
            source
          }
          data
          keys {
            key
            name
            color
            chartType
          }
          chartDataState

          chartError {
            message
            link
          }
          threshold {
            title
            value
            color
            desc
            target
          }
        }
      }
    `,
    {
      orgId: `${getOrgId()}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startDate: startDate,
      endDate: endDate,
      teamId: teamId,
      sprintId: sprintId,
      metric: metric,
      jobRole: jobRole,
    }
  );

export const getTeamDashboardDetailsGraphql = async (
  filter: {
    startDate: string;
    endDate: string;
    teamId?: string;
    sprintId?: string;
  },
  pageNumber: number,
  pageSize: number
): Promise<TeamDashboardDetailsResponse> =>
  await graphQLClient.request(
    gql`
      query TeamDashboardDetails(
        $filter: Filter!
        $pageNumber: Int
        $pageSize: Int
      ) {
        TeamDashboardDetails(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            name
            wipWithoutDevDueDate
            wipWithoutQaDueDate
            devDueDateChanged
            avgDevDueDateDelays
            avgQaDueDateDelays
            blocked
          }
          orgAverage {
            name
            wipWithoutDevDueDate
            wipWithoutQaDueDate
            devDueDateChanged
            avgDevDueDateDelays
            avgQaDueDateDelays
            blocked
          }
          totalPages
          currentPage
        }
      }
    `,
    {
      filter: {
        ...filter,
        orgId: getOrgId(),
      },
      pageNumber,
      pageSize,
    }
  );

export const teamHygieneTicketDetailsGraphql = async (
  startDate: string,
  endDate: string,
  issueCategory: string,
  filter: {
    teamId?: string;
    sprintId?: string;
    orgId?: string;
  },
  pageNumber: number,
  pageSize: number
): Promise<TeamHygieneTicketDetailsResponse> =>
  await graphQLClient.request(
    gql`
      query TeamHygieneTicketDetails(
        $filter: Filter!
        $issueCategory: String!
        $pageNumber: Int
        $pageSize: Int
      ) {
        TeamHygieneTicketDetails(
          filter: $filter
          issueCategory: $issueCategory
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            id
            summary
            assignee
            ticketType
            ticketUrl
            link
            createdAt
            updatedDate
            key
            status
            investment
            customFieldValue
            project
            estimate
            title
            subTitle
            author
            priority
            date
            attributes {
              key
              value
              color
            }
            externalLink
            storyPoint
            issueStatusData {
              label
              color
              value
              actualVal
              avgValue
            }
            sprintTaskDistList {
              type
              tasks {
                name
                value
              }
              tasksDistribution {
                color
                status
                value
              }
            }
            spilledDays
            waitingTime
            parent
            doneDate
            sprintScope
          }
          metricZeroStateDetails {
            title
            actionText
            actionNav
            description
            leftImageUrl
            topImageUrl
            bottomImageUrl
            rightImageUrl
          }
          chartError {
            message
            link
          }
          chartDataState
          totalPages
          currentPage
          totalCount
        }
      }
    `,
    {
      filter: {
        startDate,
        endDate,
        orgId: getOrgId(),
        ...filter,
      },
      issueCategory,
      pageNumber,
      pageSize,
    }
  );

export const getEngineeringStatsGraphql = async (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
  },
  pageNumber: number,
  pageSize: number
): Promise<EngineeringStatsResponse> =>
  await graphQLClient.request(
    gql`
      query GetEngineeringStats(
        $filter: Filter!
        $pageNumber: Int
        $pageSize: Int
      ) {
        EngineeringStats(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            email
            ticketCount
            totalEffort
            qAEffort
            devEffort
            products
          }
          currentPage
          totalCount
          totalPages
          chartDataState
        }
      }
    `,
    {
      filter: {
        ...filter,
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      pageNumber,
      pageSize,
    }
  );

export const getProductStatsGraphql = async (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
  },
  pageNumber: number,
  pageSize: number
): Promise<ProductStatsResponse> =>
  await graphQLClient.request(
    gql`
      query GetProductStats(
        $filter: Filter!
        $pageNumber: Int
        $pageSize: Int
      ) {
        ProductStats(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            productId
            productName
            ticketCount
            totalEffort
            engineers
          }
          currentPage
          totalCount
          totalPages
          chartDataState
        }
      }
    `,
    {
      filter: {
        ...filter,
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      pageNumber,
      pageSize,
    }
  );

export const getWorkflowStageContributorsGraphql = async (filter: {
  startDate: string;
  endDate: string;
  teamId?: string;
  sprintId?: string;
}): Promise<{
  ContributorDetailsV2: { data: { name: string; email: string }[] };
}> =>
  await graphQLClient.request(
    gql`
      query GetWorkflowStageContributors(
        $filter: Filter!
        $pageNumber: Int
        $pageSize: Int
      ) {
        ContributorDetailsV2(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            name
            email
          }
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        startDate: filter.startDate,
        endDate: filter.endDate,
        teamId: filter.teamId ?? '',
        sprintId: filter.sprintId ?? '',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      pageNumber: 0,
      pageSize: 500,
    }
  );

export const getWorkflowStageTimeGraphql = async (filter: {
  startDate: string;
  endDate: string;
  teamId?: string;
  sprintId?: string;
  epicKey?: string;
  author?: string;
}): Promise<WorkflowStageTimeResponse> =>
  await graphQLClient.request(
    gql`
      query GetWorkflowStageTimeByComponent(
        $filter: Filter!
        $epicKey: String
      ) {
        WorkflowStageTimeByComponent(filter: $filter, epicKey: $epicKey) {
          data
          chartDataState
          chartError {
            message
            link
          }
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        startDate: filter.startDate,
        endDate: filter.endDate,
        teamId: filter.teamId ?? '',
        sprintId: filter.sprintId ?? '',
        author: filter.author ?? '',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      epicKey: filter.epicKey || null,
    }
  );

export const getReworkDensityGraphql = async (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
    projectsFilter?: string[];
  },
  pageNumber: number,
  pageSize: number
): Promise<ReworkDensityResponse> =>
  await graphQLClient.request(
    gql`
      query GetReworkDensity(
        $filter: Filter!
        $pageNumber: Int
        $pageSize: Int
      ) {
        ReworkDensity(
          filter: $filter
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            contributor
            totalCommits
            reworkCommits
            reworkPercentage
          }
          currentPage
          totalCount
          totalPages
          chartDataState
        }
      }
    `,
    {
      filter: {
        ...filter,
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        projectsFilter: filter.projectsFilter ?? [],
      },
      pageNumber,
      pageSize,
    }
  );
